import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import PublicIcon from "@mui/icons-material/PublicOutlined";
import StarIcon from "@mui/icons-material/StarBorderOutlined";
import { useCallback, useEffect, useState, type CSSProperties } from "react";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { reorderIds } from "@shared/catalogue-order";
import { foyerApi, type PremiseChannelOrder } from "../http/foyer";
import { PageContainer, PageHeader } from "../components/PageHeader";
import { friendlyError } from "../utils/apiError";
import { tokens } from "../theme";

/**
 * One hotel's channel order — Streaming TV → Channels.
 *
 * ## Why a hotel needs this at all
 *
 * `Settings.channelOrder` is the national line-up, authored once by Nenda, and
 * it is the right default for almost everything: SVT1 is channel 1 in every
 * hotel in Sweden, and a channel ingested next month lands in place by itself.
 *
 * But **a hotel's own content channel cannot be on a national list by
 * definition**. It arrives unranked, sorts to the tail, and the operator drags
 * it to the first slot. That is a Nenda USP, so it is the case this screen is
 * built around rather than an afterthought.
 *
 * ## Dragging writes the WHOLE visible order
 *
 * `Premise.channelOrder` carries membership AND order, exactly like
 * `Premise.carousels`. Writing the whole list means the arrangement on screen
 * is the arrangement stored — no diffing, and no way for the two to disagree.
 *
 * A hotel that only wants its own channel first could store one id and inherit
 * the rest; the server supports that and the model documents it. This screen
 * writes the full list because that is what a drag actually means, and a UI
 * that stored less than it showed would be guessing at intent.
 *
 * ## Every row says why it is where it is
 *
 * Same rule as the Carousels screen: a computed order an operator cannot
 * account for is how a system starts feeling haunted. A row is placed by this
 * hotel, by the national list, or by nothing yet — and the third is what a new
 * channel and a hotel's own channel both look like before anybody decides.
 */
export interface PremiseChannelsPageProps {
  premiseId: string;
}

export default function PremiseChannelsPage({
  premiseId,
}: PremiseChannelsPageProps) {
  const [data, setData] = useState<PremiseChannelOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await foyerApi.premiseChannelOrder(premiseId));
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setLoading(false);
    }
  }, [premiseId]);

  useEffect(() => {
    void load();
  }, [load]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  async function save(channelOrder: string[]) {
    setError(null);
    try {
      await foyerApi.savePremiseChannelOrder(premiseId, channelOrder);
      setSaved(true);
      // Re-read rather than patching locally: `source` is recomputed server
      // side, so a row that just became "moved by this hotel" says so.
      await load();
    } catch (e) {
      setError(friendlyError(e));
      void load();
    }
  }

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || !data) return;
    const ids = data.channels.map((c) => c.id);
    const next = reorderIds(ids, String(active.id), String(over.id));
    if (!next) return;
    // Optimistic: order is the one thing the server will not change under us.
    setData({
      ...data,
      channels: next.flatMap((id) => data.channels.filter((c) => c.id === id)),
    });
    void save(next);
  };

  /** Hand the order back to the national line-up. */
  function followOfficial() {
    void save([]);
  }

  return (
    <PageContainer>
      <PageHeader
        title="Channels"
        subtitle="The order this hotel's televisions list its channels in."
      />

      {loading && (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress size={22} />
        </Stack>
      )}

      {!loading && error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && data && (
        <>
          {data.channels.length === 0 ? (
            <Typography sx={{ color: tokens.inkMuted, fontSize: 14 }}>
              This hotel has not been sold any channels yet. Channels arrive
              with a product — sell it one under Products, and they appear here
              in the national order.
            </Typography>
          ) : (
            <>
              <Alert
                severity="info"
                sx={{ mb: 2 }}
                action={
                  data.usingOfficial ? undefined : (
                    <Button
                      size="small"
                      color="inherit"
                      onClick={followOfficial}
                      data-testid="channels-follow-official"
                    >
                      Use the official order
                    </Button>
                  )
                }
              >
                {data.usingOfficial ? (
                  <>
                    This hotel follows the <strong>official line-up</strong>.
                    Drag a channel and it takes over — which is how a
                    hotel&rsquo;s own channel gets out of the bottom of the
                    list, since it cannot be on a national list.
                  </>
                ) : (
                  <>
                    This hotel has its <strong>own order</strong>. It no longer
                    moves when the official line-up changes.
                  </>
                )}
              </Alert>

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={onDragEnd}
              >
                <SortableContext
                  items={data.channels.map((c) => c.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <Stack spacing={1} data-testid="premise-channels">
                    {data.channels.map((channel, index) => (
                      <SortableChannelRow
                        key={channel.id}
                        channel={channel}
                        index={index}
                      />
                    ))}
                  </Stack>
                </SortableContext>
              </DndContext>
            </>
          )}
        </>
      )}

      <Snackbar
        open={saved}
        autoHideDuration={2000}
        onClose={() => setSaved(false)}
        message="Saved"
      />
    </PageContainer>
  );
}

type Channel = PremiseChannelOrder["channels"][number];

function SortableChannelRow(props: { channel: Channel; index: number }) {
  const sortable = useSortable({ id: props.channel.id });
  return (
    <ChannelRow
      {...props}
      setNodeRef={sortable.setNodeRef}
      style={{
        transform: CSS.Transform.toString(sortable.transform),
        transition: sortable.transition,
        opacity: sortable.isDragging ? 0.4 : undefined,
      }}
      grip={{ ...sortable.attributes, ...sortable.listeners }}
    />
  );
}

function ChannelRow({
  channel,
  index,
  setNodeRef,
  style,
  grip,
}: {
  channel: Channel;
  index: number;
  setNodeRef?: (el: HTMLElement | null) => void;
  style?: CSSProperties;
  grip?: Record<string, unknown>;
}) {
  return (
    <Stack
      ref={setNodeRef}
      style={style}
      direction="row"
      alignItems="center"
      spacing={1.5}
      data-testid={`premise-channel-${index}`}
      sx={{
        px: 1.5,
        py: 1.25,
        borderRadius: "12px",
        border: "1px solid",
        borderColor: tokens.divider,
        bgcolor: tokens.surface,
      }}
    >
      <Tooltip title="Drag to reorder">
        <IconButton
          size="small"
          {...grip}
          aria-label={`Reorder ${channel.name}`}
          sx={{ cursor: "grab" }}
        >
          <DragIndicatorIcon fontSize="small" sx={{ color: tokens.inkFaint }} />
        </IconButton>
      </Tooltip>

      <Typography
        sx={{
          width: 22,
          textAlign: "right",
          fontSize: 12,
          fontVariantNumeric: "tabular-nums",
          color: tokens.inkFaint,
        }}
      >
        {index + 1}
      </Typography>

      <Typography
        sx={{ flex: 1, minWidth: 0, fontWeight: 600, fontSize: 14 }}
        noWrap
      >
        {channel.name}
      </Typography>

      {/* Every row accounts for itself — the same rule the Carousels screen
          follows. "Unranked" is not a fault: it is what a hotel's own channel
          and a newly ingested one both look like before anybody decides. */}
      {channel.source === "own" && (
        <Chip
          size="small"
          icon={<StarIcon sx={{ fontSize: 14 }} />}
          label="Placed by this hotel"
          sx={{ fontSize: 11 }}
        />
      )}
      {channel.source === "official" && (
        <Chip
          size="small"
          variant="outlined"
          icon={<PublicIcon sx={{ fontSize: 14 }} />}
          label="Official line-up"
          sx={{ fontSize: 11 }}
        />
      )}
      {channel.source === "unranked" && (
        <Tooltip title="On neither list, so it sits after everything that is on one. Drag it up to give it a place.">
          <Chip
            size="small"
            variant="outlined"
            label="Not placed yet"
            sx={{ fontSize: 11, color: tokens.inkMuted }}
          />
        </Tooltip>
      )}
    </Stack>
  );
}
